import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import QuizComponent from './components/QuizComponent';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorMessage from './components/ErrorMessage';
import SuccessMessage from './components/SuccessMessage';
import AlreadyCompletedMessage from './components/AlreadyCompletedMessage';
import { fetchCurrentQuiz } from './services/quizService';

const AppContainer = styled.div`
  min-height: 100vh;
  background-color: var(--tg-theme-bg-color, #ffffff);
  color: var(--tg-theme-text-color, #000000);
  padding: 16px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 24px;
  padding: 16px 0;
  border-bottom: 1px solid var(--tg-theme-separator-color, #e5e5e5);
`;

const Title = styled.h1`
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: var(--tg-theme-text-color, #000000);
`;

const Subtitle = styled.p`
  margin: 8px 0 0 0;
  font-size: 16px;
  color: var(--tg-theme-hint-color, #999999);
`;

const NoQuizMessage = styled.div`
  text-align: center;
  padding: 48px 16px;
  color: var(--tg-theme-hint-color, #999999);
  
  h2 {
    margin: 0 0 16px 0;
    font-size: 20px;
    color: var(--tg-theme-text-color, #000000);
  }
  
  p {
    margin: 0;
    font-size: 16px;
    line-height: 1.5;
  }
`;

function App() {
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [userId, setUserId] = useState(null);
  const [alreadyCompleted, setAlreadyCompleted] = useState(false);
  const [completedQuizName, setCompletedQuizName] = useState(null);

  useEffect(() => {
    // Проверяем, открыто ли приложение через Telegram WebApp
    if (!window.Telegram?.WebApp || !window.Telegram.WebApp.initData) {
      setError('Приложение должно быть открыто через Telegram бот');
      setLoading(false);
      return;
    }

    // Инициализация Telegram WebApp
    const tg = window.Telegram.WebApp;
    tg.ready();
    tg.expand();
    
    // Получаем ID пользователя из Telegram
    if (tg.initDataUnsafe?.user?.id) {
      setUserId(tg.initDataUnsafe.user.id);
    } else {
      setError('Не удалось получить данные пользователя из Telegram');
      setLoading(false);
      return;
    }
    
    // Настраиваем цвета приложения
    tg.setHeaderColor(tg.themeParams.bg_color || '#ffffff');

    loadQuiz();
  }, []);

  const loadQuiz = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchCurrentQuiz();
      
      // Проверяем, что ответ корректный и содержит необходимые данные
      if (!response || typeof response !== 'object') {
        throw new Error('Некорректный ответ от сервера');
      }
      
      // Проверяем, прошел ли пользователь уже опросник
      if (response.already_completed) {
        setAlreadyCompleted(true);
        setCompletedQuizName(response.quiz_name);
        setQuiz(null);
        return;
      }
      
      // Проверяем наличие квиза в ответе
      if (response.quiz) {
        setQuiz(response.quiz);
      } else {
        setQuiz(null);
      }
    } catch (err) {
      console.error('Ошибка загрузки опросника:', err);
      setError(err.message || 'Не удалось загрузить опросник. Попробуйте позже.');
    } finally {
      setLoading(false);
    }
  };

  const handleQuizComplete = () => {
    setSuccess(true);
    setQuiz(null);
    
    // Закрыть WebApp через несколько секунд
    setTimeout(() => {
      if (window.Telegram?.WebApp) {
        window.Telegram.WebApp.close();
      }
    }, 3000);
  };

  const handleError = (errorMessage) => {
    setError(errorMessage);
  };

  if (loading) {
    return (
      <AppContainer>
        <Header>
          <Title>Загрузка опросника...</Title>
        </Header>
        <LoadingSpinner />
      </AppContainer>
    );
  }

  if (error) {
    return (
      <AppContainer>
        <Header>
          <Title>Ошибка</Title>
        </Header>
        <ErrorMessage message={error} onRetry={loadQuiz} />
      </AppContainer>
    );
  }

  if (success) {
    return (
      <AppContainer>
        <Header>
          <Title>Спасибо!</Title>
        </Header>
        <SuccessMessage />
      </AppContainer>
    );
  }

  if (alreadyCompleted) {
    return (
      <AppContainer>
        <Header>
          <Title>Опрос завершен</Title>
        </Header>
        <AlreadyCompletedMessage quizName={completedQuizName} />
      </AppContainer>
    );
  }

  if (!quiz) {
    return (
      <AppContainer>
        <Header>
          <Title>Опросник</Title>
        </Header>
        <NoQuizMessage>
          <h2>Нет доступных опросников</h2>
          <p>В данный момент нет активных опросников для прохождения.</p>
        </NoQuizMessage>
      </AppContainer>
    );
  }

  return (
    <AppContainer>
      <Header>
        <Title>{quiz.name}</Title>
        <Subtitle>Ответьте на {quiz.questions.length} вопросов</Subtitle>
      </Header>
      <QuizComponent 
        quiz={quiz}
        userId={userId}
        onComplete={handleQuizComplete}
        onError={handleError}
      />
    </AppContainer>
  );
}

export default App; 